import {AsyncPipe, DOCUMENT} from '@angular/common';
import {afterNextRender, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Injector, Input, Output, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {fromEvent} from 'rxjs';
import {OnboardingNavigationService} from '@src/app/core/services/onboarding-navigation.service';

@Component({
    selector: 'app-onboarding-navigation',
    imports: [AsyncPipe, MatButtonModule, MatIconModule, MatProgressBarModule],
    template: `
        @if (navigation.state$ | async; as state) {
            @if (state.inDialog === embedded) {
                <section #coach class="navigation-coach mat-elevation-z4" [class.navigation-coach--anchored]="state.ready && !embedded" [class.navigation-coach--embedded]="embedded" [attr.data-placement]="placement" [style.top.px]="state.ready && !embedded ? top : null" [style.left.px]="state.ready && !embedded ? left : null" [style.--coach-arrow-offset]="arrowOffset + 'px'" [style.visibility]="state.ready && !embedded && !positioned ? 'hidden' : null" role="region" aria-labelledby="navigation-coach-title">
                    <header>
                        <h2 id="navigation-coach-title">{{ state.title }}</h2>
                        <button mat-icon-button type="button" (click)="navigation.stop()" aria-label="Close guidance"><mat-icon>close</mat-icon></button>
                    </header>
                    <p id="onboarding-navigation-instruction" role="status" aria-live="polite">{{ state.unavailable ? 'This control isn’t available here yet.' : state.instruction }}</p>
                    <footer>
                        @if (!embedded) { <button mat-button type="button" class="coach-back" (click)="backToTasks()"><mat-icon aria-hidden="true">chevron_left</mat-icon>All tasks</button> }
                        @if (state.unavailable) { <button mat-button type="button" (click)="navigation.retry()">Try again</button> }
                    </footer>
                    @if (!state.ready && !state.unavailable) { <mat-progress-bar class="coach-progress" mode="indeterminate" aria-hidden="true"></mat-progress-bar> }
                </section>
            }
        }
    `,
    styleUrls: ['./onboarding-navigation.component.scss'],
})
export class OnboardingNavigationComponent {
    public readonly navigation = inject(OnboardingNavigationService);
    private readonly injector = inject(Injector);
    private readonly document = inject(DOCUMENT);
    private readonly changes = inject(ChangeDetectorRef);
    private anchored = false;
    @ViewChild('coach') private coach?: ElementRef<HTMLElement>;
    @Input() public embedded = false;
    @Output() public recalled = new EventEmitter<void>();
    public top = 0;
    public left = 0;
    public arrowOffset = 24;
    public placement = 'below';
    public positioned = false;

    public constructor() {
        this.navigation.state$.pipe(takeUntilDestroyed()).subscribe(state => {
            this.anchored = !!state?.ready && !state.inDialog;
            this.positioned = false;
            if (this.anchored) afterNextRender(() => this.position(), {injector: this.injector});
        });
        for (const event of ['scroll', 'animationend', 'transitionend']) {
            fromEvent(this.document, event, {capture: true}).pipe(takeUntilDestroyed()).subscribe(() => this.position());
        }
        if (this.document.defaultView) fromEvent(this.document.defaultView, 'resize').pipe(takeUntilDestroyed()).subscribe(() => this.position());
    }

    private position(): void {
        const element = this.navigation.targetElement;
        const coach = this.coach?.nativeElement;
        const view = this.document.defaultView;
        if (!this.anchored || this.embedded || !element || !coach || !view) return;
        const target = element.getBoundingClientRect();
        const popup = coach.getBoundingClientRect();
        const gap = 12;
        const margin = 12;
        const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, Math.max(min, max)));
        const centerX = target.left + target.width / 2;
        const centerY = target.top + target.height / 2;
        const menu = element.closest('[role="menu"]')?.getBoundingClientRect();
        if (element.matches('input, textarea, select, mat-select') && target.top - gap - popup.height >= margin) {
            this.placement = 'above';
            this.top = target.top - gap - popup.height;
            this.left = clamp(centerX - popup.width / 2, margin, view.innerWidth - popup.width - margin);
        } else if (menu && menu.left - gap - popup.width >= margin) {
            this.placement = 'left';
            this.left = menu.left - gap - popup.width;
            this.top = clamp(centerY - popup.height / 2, margin, view.innerHeight - popup.height - margin);
        } else if (menu && menu.right + gap + popup.width <= view.innerWidth - margin) {
            this.placement = 'right';
            this.left = menu.right + gap;
            this.top = clamp(centerY - popup.height / 2, margin, view.innerHeight - popup.height - margin);
        } else if (menu && menu.bottom + gap + popup.height <= view.innerHeight - margin) {
            this.placement = 'below';
            this.top = menu.bottom + gap;
            this.left = clamp(centerX - popup.width / 2, margin, view.innerWidth - popup.width - margin);
        } else if (target.bottom + gap + popup.height <= view.innerHeight - margin) {
            this.placement = 'below';
            this.top = target.bottom + gap;
            this.left = clamp(centerX - popup.width / 2, margin, view.innerWidth - popup.width - margin);
        } else if (target.right + gap + popup.width <= view.innerWidth - margin) {
            this.placement = 'right';
            this.left = target.right + gap;
            this.top = clamp(centerY - popup.height / 2, margin, view.innerHeight - popup.height - margin);
        } else if (target.left - gap - popup.width >= margin) {
            this.placement = 'left';
            this.left = target.left - gap - popup.width;
            this.top = clamp(centerY - popup.height / 2, margin, view.innerHeight - popup.height - margin);
        } else {
            this.placement = 'above';
            this.top = clamp(target.top - gap - popup.height, margin, view.innerHeight - popup.height - margin);
            this.left = clamp(centerX - popup.width / 2, margin, view.innerWidth - popup.width - margin);
        }
        this.arrowOffset = this.placement === 'above' || this.placement === 'below'
            ? clamp(centerX - this.left, 20, popup.width - 20)
            : clamp(centerY - this.top, 20, popup.height - 20);
        this.positioned = true;
        this.changes.detectChanges();
    }

    public backToTasks(): void {
        this.navigation.stop(false);
        this.recalled.emit();
    }
}
